import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polygon, FeatureGroup, Popup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { baseurladdress } from "../baseurl";
import * as turf from '@turf/turf';
import Swal from 'sweetalert2';

// Component to fit map bounds to region
function MapBoundsFitter({ bounds }) {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);

  return null;
}

function AreaManagement() {
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [areaName, setAreaName] = useState('');
  const [drawnPolygon, setDrawnPolygon] = useState(null);
  const [showAreas, setShowAreas] = useState(true);
  const featureGroupRef = useRef();

  // Fetch regions on component mount
  useEffect(() => {
    fetchRegions();
    fetchAreas();
    fetchStaffs();
  }, []);

  const fetchRegions = async () => {
    try {
      const response = await fetch(baseurladdress + 'region/getregions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.status === 'Success') {
        setRegions(data.regions);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch regions'
      });
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await fetch(baseurladdress + 'area/getareas', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.status === 'Success') {
        setAreas(data.areas);
      }
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  const fetchStaffs = async () => {
    try {
      const response = await fetch(baseurladdress + 'staff/getstaffs', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.status === 'Success') {
        setStaffs(data.staffs || []);
      }
    } catch (error) {
      console.error('Error fetching staffs:', error);
    }
  };

  const getStaffsForArea = (areaId) => {
    return staffs.filter(staff => {
      if (!staff.staff_area) return false;
      const areaStr = String(staff.staff_area);
      const areas = areaStr.split(',').map(a => a.trim());
      return areas.includes(areaId.toString());
    });
  };

  const getStaffsForRegion = (regionId) => {
    return staffs.filter(staff => {
      if (!staff.staff_region) return false;
      return staff.staff_region === regionId || staff.staff_region === regionId.toString();
    });
  };

  const handleRegionSelect = (e) => {
    const regionId = e.target.value;
    const region = regions.find(r => r.region_id === parseInt(regionId));
    setSelectedRegion(region);

    // Clear any existing drawn polygon
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
    setDrawnPolygon(null);
  };

  const handleCreated = (e) => {
    const layer = e.layer;

    const coords = layer.getLatLngs()[0].map(latLng => ({
      lat: latLng.lat,
      lng: latLng.lng
    }));

    const newPolygon = turf.polygon([coords.map(p => [p.lng, p.lat]).concat([[coords[0].lng, coords[0].lat]])]);

    // Check overlap with existing areas
    for (const area of areas) {
      const areaCoords = JSON.parse(area.area_polygon).map(p => [p.lng, p.lat]);
      areaCoords.push(areaCoords[0]);
      const existingPolygon = turf.polygon([areaCoords]);

      if (turf.booleanIntersects(newPolygon, existingPolygon)) {
        Swal.fire({
          icon: 'error',
          title: 'Overlap detected',
          text: `This area overlaps with "${area.area_name}"`,
        });
        featureGroupRef.current.removeLayer(layer);
        return;
      }
    }

    // Check within selected region
    if (selectedRegion) {
      const regionCoords = JSON.parse(selectedRegion.region_polygon).map(p => [p.lng, p.lat]);
      regionCoords.push(regionCoords[0]);
      const regionPolygon = turf.polygon([regionCoords]);

      if (!turf.booleanWithin(newPolygon, regionPolygon)) {
        Swal.fire({
          icon: 'error',
          title: 'Out of bounds',
          text: `Area must be within the selected region "${selectedRegion.region_name}"`,
        });
        featureGroupRef.current.removeLayer(layer);
        return;
      }
    }

    // No overlap: save polygon
    featureGroupRef.current.clearLayers();
    featureGroupRef.current.addLayer(layer);
    setDrawnPolygon(coords);
  };

  const handleDeleted = () => {
    setDrawnPolygon(null);
  };

  const handleSubmit = async () => {
    if (!areaName.trim()) {
      Swal.fire({ icon: 'warning', title: 'Missing Area Name', text: 'Please enter an area name' });
      return;
    }
    if (!selectedRegion) {
      Swal.fire({ icon: 'warning', title: 'No Region Selected', text: 'Please select a region' });
      return;
    }
    if (!drawnPolygon) {
      Swal.fire({ icon: 'warning', title: 'No Area Drawn', text: 'Please draw the area on the map' });
      return;
    }

    try {
      const response = await fetch(baseurladdress + 'area/addarea', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          area_name: areaName,
          region_id: selectedRegion.region_id,
          polygon: drawnPolygon,
          userid: localStorage.getItem('id'),
        }),
      });

      const data = await response.json();
      if (data.status === 'Success') {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Area added successfully!',
          showConfirmButton: false,
          timer: 1800,
        });

        // Reset inputs
        setAreaName('');
        setDrawnPolygon(null);
        if (featureGroupRef.current) featureGroupRef.current.clearLayers();

        // Refresh areas and regions
        fetchAreas();
        fetchRegions();
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to add area' });
      }
    } catch (error) {
      console.error('Error adding area:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Error adding area' });
    }
  };

  const handleDeleteArea = async (areaId) => {
    if (!window.confirm('Are you sure you want to delete this area?')) return;

    try {
      const response = await fetch(baseurladdress + `area/deletearea/${areaId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (data.status === 'success') {
        alert('Area deleted successfully!');
        fetchAreas();
      } else {
        alert(data.message || 'Failed to delete area');
      }
    } catch (error) {
      console.error('Error deleting area:', error);
      alert('Error deleting area');
    }
  };

  // Calculate bounds for selected region
  const getRegionBounds = () => {
    if (!selectedRegion) return null;

    const polygon = JSON.parse(selectedRegion.region_polygon);
    const lats = polygon.map(p => p.lat);
    const lngs = polygon.map(p => p.lng);

    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)]
    ];
  };

  // Responsive styles
  const containerStyle = {
    padding: '15px',
    maxWidth: '1400px',
    margin: '0 auto',
  };

  const headingStyle = {
    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
    marginBottom: '20px',
    color: '#333',
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '20px',
  };

  const formPanelStyle = {
    marginBottom: '20px',
    padding: '20px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#fff',
  };

  const formGroupStyle = {
    marginBottom: '15px',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    fontSize: 'clamp(0.9rem, 2vw, 1rem)',
    color: '#555',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    boxSizing: 'border-box',
    transition: 'border-color 0.3s',
  };

  const infoBoxStyle = {
    marginBottom: '15px',
    padding: '12px',
    backgroundColor: '#f0f8ff',
    borderRadius: '6px',
    border: '1px solid #d0e8ff',
  };

  const buttonStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  };

  const mapContainerStyle = {
    border: '1px solid #ddd',
    borderRadius: '8px',
    overflow: 'hidden',
  };

  const mapStyle = {
    height: 'clamp(400px, 60vh, 700px)',
    width: '100%',
  };

  return (
    <div style={containerStyle}>
      <h2 style={headingStyle}>Area Management</h2>

      <div style={gridStyle} className="area-grid">
        {/* Left Panel - Form */}
        <div>
          <div style={formPanelStyle}>

            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Region *
              </label>
              <select
                value={selectedRegion?.region_id || ''}
                onChange={handleRegionSelect}
                style={inputStyle}
              >
                <option value="">Select a region</option>
                {regions.map(region => (
                  <option key={region.region_id} value={region.region_id}>
                    {region.region_name}
                  </option>
                ))}
              </select>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Area Name *
              </label>
              <input
                type="text"
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                placeholder="Enter area name"
                style={inputStyle}
              />
            </div>

            <div style={infoBoxStyle}>
              <p style={{ margin: '0', fontSize: 'clamp(0.85rem, 2vw, 0.95rem)', color: '#0066cc', fontWeight: '500' }}>
                📍 {selectedRegion
                  ? `Selected: ${selectedRegion.region_name}`
                  : 'Select a region to see it on the map'}
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: 'clamp(0.75rem, 1.8vw, 0.85rem)', color: '#666', lineHeight: '1.4' }}>
                Click on regions/areas to view details. Use the polygon tool to draw the area within the highlighted region.
              </p>
            </div>

            <button
              onClick={handleSubmit}
              style={buttonStyle}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
            >
              Add Area
            </button>
          </div>
        </div>

        {/* Right Panel - Map */}
        <div style={mapContainerStyle}>
          <MapContainer
            center={[10.8505, 76.2711]}
            zoom={10}
            style={mapStyle}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />

           
            {/* Show all regions on map */}
            {regions.map(region => {
              const polygon = JSON.parse(region.region_polygon);
              const positions = polygon.map(p => [p.lat, p.lng]);
              const isSelected = selectedRegion?.region_id === region.region_id;
              const areasInRegion = areas.filter(a => a.aregion_id === region.region_id);

              return (
                <Polygon
                  key={`region-${region.region_id}`}
                  positions={positions}
                  pathOptions={{
                    color: isSelected ? '#ff6b6b' : '#4a90e2',
                    fillColor: isSelected ? '#ff6b6b' : '#4a90e2',
                    fillOpacity: isSelected ? 0.3 : 0.1,
                    weight: isSelected ? 3 : 2
                  }}
                >
                  <Popup>
                    <div style={{ minWidth: '180px', maxWidth: '300px' }}>
                      <h4 style={{ margin: '0 0 10px 0', color: '#333', fontSize: 'clamp(0.95rem, 2vw, 1.1rem)' }}>
                        {region.region_name}
                      </h4>

                      <div style={{ marginBottom: '8px', fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)' }}>
                        <strong>Region Manager:</strong> {region.region_manager_name || "Not Assigned"}
                      </div>

                      <div style={{ fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)' }}>
                        <strong>Areas:</strong>
                        {areasInRegion.length > 0 ? (
                          <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                            {areasInRegion.map(area => (
                              <li key={area.area_id} style={{ marginBottom: '3px' }}>
                                {area.area_name}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div style={{ color: '#999', marginTop: '2px', fontStyle: 'italic' }}>
                            No areas added yet
                          </div>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Polygon>
              );
            })}

            {/* Show existing areas if checkbox is checked */}
            {showAreas && areas.map(area => {
              const polygon = JSON.parse(area.area_polygon);
              const positions = polygon.map(p => [p.lat, p.lng]);
              const staffsInArea = getStaffsForArea(area.area_id);

              return (
                <Polygon
                  key={`area-${area.area_id}`}
                  positions={positions}
                  pathOptions={{
                    color: '#28a745',
                    fillColor: '#28a745',
                    fillOpacity: 0.2,
                    weight: 2
                  }}
                >
                  <Popup>
                    <div style={{ minWidth: '180px', maxWidth: '300px' }}>
                      <h4 style={{ margin: '0 0 10px 0', color: '#333', fontSize: 'clamp(0.95rem, 2vw, 1.1rem)' }}>
                        {area.area_name}
                      </h4>

                      <div style={{ marginBottom: '8px', fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)' }}>
                        <strong>Area Manager:</strong> {area.area_manager_name || "Not Assigned"}
                      </div>

                      <div style={{ fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)' }}>
                        <strong>Staffs:</strong>
                        {staffsInArea.length > 0 ? (
                          <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                            {staffsInArea.map(staff => (
                              <li key={staff.staff_id} style={{ marginBottom: '3px' }}>
                                {staff.staff_name}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div style={{ color: '#999', marginTop: '2px', fontStyle: 'italic' }}>
                            No staffs assigned yet
                          </div>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Polygon>
              );
            })}

            {/* Drawing controls */}
            <FeatureGroup ref={featureGroupRef}>
              <EditControl
                position="topright"
                onCreated={handleCreated}
                onDeleted={handleDeleted}
                draw={{
                  rectangle: false,
                  circle: false,
                  circlemarker: false,
                  marker: false,
                  polyline: false,
                  polygon: {
                    allowIntersection: false,
                    shapeOptions: {
                      color: '#ffc107',
                      fillColor: '#ffc107',
                      fillOpacity: 0.4,
                      weight: 3
                    }
                  }
                }}
                edit={{
                  edit: false,
                  remove: true
                }}
              />
            </FeatureGroup>

            {/* Fit bounds to selected region */}
            {selectedRegion && <MapBoundsFitter bounds={getRegionBounds()} />}
          </MapContainer>
        </div>
      </div>

      <style>{`
        /* Responsive grid layout */
        @media (min-width: 992px) {
          .area-grid {
            grid-template-columns: 1fr 2fr !important;
          }
        }

        /* Map controls positioning */
        @media (max-width: 768px) {
          .leaflet-control-container .leaflet-top.leaflet-right {
            top: 10px;
            right: 10px;
          }
          
          .leaflet-draw-toolbar {
            margin-top: 0 !important;
          }
        }

        /* Input focus states */
        input:focus,
        select:focus {
          outline: none;
          border-color: #28a745 !important;
          box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
        }

        /* Button hover state */
        button:hover:not(:disabled) {
          opacity: 0.9;
        }

        /* Better scrolling on mobile */
        @media (max-width: 768px) {
          .leaflet-container {
            touch-action: pan-x pan-y;
          }
        }
      `}</style>
    </div>
  );
}

export default AreaManagement;